import type { HTMLAttributes, ReactElement } from 'react';

import { classNames } from '@m1work/shared-utils';

import styles from './EditorText.module.scss';

export type EditorTextSize = 'sm' | 'md' | 'lg';

export interface EditorTextProps extends HTMLAttributes<HTMLDivElement> {
    /** **Sanitized** HTML from a rich-text editor (CMS). The component injects it as is; the app must clean it first. */
    value: string | null;
    /** Base text size of the markup. @default 'md' */
    size?: EditorTextSize;
}

/**
 * Renders rich-text HTML from the CMS (paragraphs, lists, links, quotes) with the design's typography.
 * Renders nothing for an empty value. It does not sanitize: pass HTML the app has already cleaned.
 *
 * @example
 * <EditorText value={sanitizeHtml(product.description)} size="sm" />
 */
export function EditorText({ value, size = 'md', className, ...props }: EditorTextProps): ReactElement | null {
    if (!value || value.trim().length === 0) {
        return null;
    }

    return (
        <div
            className={classNames(styles['rich-text'], styles[`size-${size}`], className)}
            // The value is sanitized by the app (see `value`).
            dangerouslySetInnerHTML={{ __html: value }}
            {...props}
        />
    );
}
